/**
 * Home-Seite (Kommentare D — „Home ist immer noch = meine Dateien"). Eine echte
 * Landing-Seite statt eines Redirects: Schnell-Aktionen (Datei hinzufügen, in die
 * Bereiche springen) und die zuletzt geöffneten/geänderten Dateien. Reiner
 * Host-Layer (D-04). Öffnet Dateien über dieselbe route-by-kind-Weiche wie die
 * Dateiliste — ein unbekannter kind hat keinen Editor.
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError, ryfeApi } from "../api";
import { serializeScene } from "../board-data";
import { createUrlBookmark, uploadFileToFolder } from "../file-actions";
import { routeForKind } from "../open-file";
import { useRyfe } from "../RyfeContext";
import { navigate } from "../router";

import { AddFileDialog, type NewEntryKind } from "./AddFileDialog";

import "./ryfe.scss";

import type { FileMeta, TemplateFile } from "../api";

const KIND_ICON: Record<string, string> = {
  board: "🧩",
  md: "📝",
  list: "🗂",
  image: "🖼",
};

const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString() : "—";

export const RyfeHome = () => {
  const { workspace, user } = useRyfe();
  const wsId = workspace?.id;
  const [recent, setRecent] = useState<FileMeta[] | null>(null);
  const [rootId, setRootId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // P12 — „Aus Vorlage erstellen" auch vom Home (nicht nur in „Meine Dateien").
  const [templates, setTemplates] = useState<TemplateFile[]>([]);

  const load = useCallback(async () => {
    if (!wsId) {
      return;
    }
    try {
      const root = await ryfeApi.getRoot(wsId);
      setRootId(root.id);
      // P12 — Vorlagen des Workspace laden (für die „Aus Vorlage"-Wahl).
      ryfeApi
        .listTemplates(wsId)
        .then((t) => setTemplates(t.templates))
        .catch(() => setTemplates([]));
      // R4 (Zwischenfeedback) — „Zuletzt" über ALLE Unterordner (eigener,
      // guard-gefilterter Endpoint); vorher nur die Dateien im Root.
      const r = await ryfeApi.listRecentFiles(wsId);
      setRecent(r.files.slice(0, 8));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Laden fehlgeschlagen");
    }
  }, [wsId]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (f: FileMeta) => {
    const route = routeForKind(f.kind, f.id);
    if (route) {
      navigate(route);
    }
  };

  // P12 — eine Vorlage in den Root des eigenen Workspace forken und öffnen.
  const createFromTemplate = async (templateId: string, name: string) => {
    if (!wsId || !rootId) {
      return;
    }
    setBusy(true);
    try {
      const created = await ryfeApi.forkFile(wsId, templateId, {
        folderId: rootId,
        name,
      });
      setAdding(false);
      const route = routeForKind(created.kind, created.id);
      if (route) {
        navigate(route);
      } else {
        await load();
      }
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : "Aus Vorlage fehlgeschlagen",
      );
    } finally {
      setBusy(false);
    }
  };

  const createEntry = async (kind: NewEntryKind, name: string) => {
    if (!wsId || !rootId) {
      return;
    }
    setBusy(true);
    try {
      if (kind === "folder") {
        await ryfeApi.createFolder(wsId, rootId, name);
        setAdding(false);
        await load();
      } else if (kind === "md") {
        const f = await ryfeApi.createFile(
          wsId,
          rootId,
          name,
          "md",
          `# ${name}\n\n`,
          "text/markdown",
        );
        navigate(`/md/${f.id}`);
      } else if (kind === "list") {
        const f = await ryfeApi.createFile(wsId, rootId, name, "list", "");
        navigate(`/list/${f.id}`);
      } else {
        const f = await ryfeApi.createFile(
          wsId,
          rootId,
          name,
          "board",
          serializeScene([], {}, {}),
        );
        navigate(`/board/${f.id}`);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Anlegen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  // Welle B (Punkt 10) — URL-Lesezeichen im Root anlegen.
  const createUrl = async (name: string, url: string) => {
    if (!wsId || !rootId) {
      return;
    }
    setBusy(true);
    try {
      await createUrlBookmark(wsId, rootId, name, url);
      setAdding(false);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Anlegen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  // Welle B (Punkt 10) — Datei hochladen (Format→Typ via upload-map).
  const upload = async (file: File) => {
    if (!wsId || !rootId) {
      return;
    }
    setBusy(true);
    try {
      const created = await uploadFileToFolder(wsId, rootId, file);
      setAdding(false);
      const route = routeForKind(created.kind, created.id);
      if (route) {
        navigate(route);
      } else {
        await load();
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ryfe-page ryfe-home">
      <h1 className="ryfe-page__title">
        Willkommen{user?.email ? `, ${user.email.split("@")[0]}` : ""}
      </h1>

      {error && <div className="ryfe-auth__error">{error}</div>}

      <div className="ryfe-home__actions">
        <button
          className="ryfe-btn ryfe-btn--primary"
          onClick={() => setAdding(true)}
        >
          + Datei hinzufügen
        </button>
        <button
          className="ryfe-btn ryfe-btn--ghost"
          onClick={() => navigate("/files")}
        >
          📁 Meine Dateien
        </button>
        <button
          className="ryfe-btn ryfe-btn--ghost"
          onClick={() => navigate("/shared-with-me")}
        >
          👥 Für mich freigegeben
        </button>
        <button
          className="ryfe-btn ryfe-btn--ghost"
          onClick={() => navigate("/library")}
        >
          📚 Meine Bibliothek
        </button>
      </div>

      <h2 className="ryfe-home__subtitle">Zuletzt</h2>
      {!recent ? (
        <div className="ryfe-muted">Laden…</div>
      ) : recent.length === 0 ? (
        <div className="ryfe-muted">
          Noch keine Dateien — „+ Datei hinzufügen" legt die erste an.
        </div>
      ) : (
        <div className="ryfe-home__grid">
          {recent.map((f) => (
            <button
              key={f.id}
              className="ryfe-home__card"
              onClick={() => open(f)}
              disabled={!routeForKind(f.kind, f.id)}
              title={f.kind}
            >
              <span className="ryfe-home__card-icon">
                {KIND_ICON[f.kind] || "📄"}
              </span>
              <span className="ryfe-home__card-name">{f.name}</span>
              <span className="ryfe-home__card-meta">
                {fmtDate(f.last_opened_at || f.updated_at)}
              </span>
            </button>
          ))}
        </div>
      )}

      {adding && (
        <AddFileDialog
          busy={busy}
          onClose={() => setAdding(false)}
          onCreate={createEntry}
          onCreateUrl={createUrl}
          onUpload={upload}
          templates={templates}
          onCreateFromTemplate={createFromTemplate}
        />
      )}
    </div>
  );
};
